/**
 * Bills — the history view.
 *
 * Per bill, every instance with its due date, the expected amount, what was
 * actually paid, when, by whom, and what is still outstanding (docs/01 §7.1).
 *
 * **Status is derived, never stored.** `paid`, `outstanding`, `unpaid /
 * partial / paid` and `overdue` are all computed here and now by
 * `billHistory`, from the payments that happen to have synced. Nothing in the
 * mirror carries a status column to go stale.
 *
 * Overdue is AMBER, never red (docs/03). A bill nobody has got round to is not
 * an error.
 */

import { useMemo, useState } from 'react'
import type { Bill, BillId, BillInstance, BillInstanceView, TrustedClock } from '@office/domain'
import { type BillHistory, billHistory } from '@office/domain/money'
import { type MoneySnapshot, nameOf } from './data.js'
import { describeDue, formatDay, formatInstant } from './format.js'
import { Badge, Empty, Money, StatusBadge } from './ui.js'
import './money.css'

export interface BillsScreenProps {
  snapshot: MoneySnapshot
  clock: TrustedClock
  /** Opens the payment form for one instance. */
  onRecordPayment?: (bill: Bill, instance: BillInstance) => void
  /** Which bill starts expanded. All are collapsed by default. */
  initialOpenBillId?: BillId
}

export function BillsScreen({
  snapshot,
  clock,
  onRecordPayment,
  initialOpenBillId,
}: BillsScreenProps) {
  const now = clock.now()

  const histories: BillHistory[] = useMemo(
    () => snapshot.bills.map(b => billHistory(b, snapshot.instances, snapshot.payments, now)),
    [snapshot, now],
  )

  const [open, setOpen] = useState<string | null>(initialOpenBillId ?? null)

  if (histories.length === 0) {
    return (
      <div className="m-screen" data-testid="bills-screen">
        <h2 className="m-title">Bills</h2>
        <Empty mark="📄" hint="Bills are templates — rent, internet, electricity. Each one materialises a dated instance every cycle.">
          No bills yet.
        </Empty>
      </div>
    )
  }

  return (
    <div className="m-screen" data-testid="bills-screen">
      <h2 className="m-title">Bills</h2>
      <p className="m-asof">as of {formatInstant(now)}</p>

      <ul className="m-list" aria-label="Bills">
        {histories.map(h => {
          const isOpen = open === h.bill.id
          return (
            <li key={h.bill.id} className="m-bill">
              <button
                type="button"
                className="m-bill-head"
                aria-expanded={isOpen}
                aria-controls={`bill-${h.bill.id}`}
                onClick={() => setOpen(isOpen ? null : h.bill.id)}
              >
                <span className="m-bill-name">{h.bill.name}</span>
                <span className="m-bill-meta">
                  {h.bill.cycle} · {h.bill.category} · <Money value={h.bill.amountExpected} /> expected
                </span>
                <span className="m-bill-side">
                  {h.totalOutstanding > 0 ? (
                    <>
                      <Money value={h.totalOutstanding} /> outstanding
                    </>
                  ) : (
                    'all settled'
                  )}
                  {h.overdueCount > 0 && (
                    <Badge tone="warn">
                      {h.overdueCount} overdue
                    </Badge>
                  )}
                </span>
              </button>

              {isOpen && (
                <div id={`bill-${h.bill.id}`} className="m-bill-body">
                  {h.views.length === 0 ? (
                    <Empty hint="Instances are materialised twelve months ahead, so both history and the year to come stay browsable offline.">
                      No instances of this bill have been generated yet.
                    </Empty>
                  ) : (
                    <ul className="m-list" aria-label={`${h.bill.name} history`}>
                      {[...h.views].reverse().map(view => (
                        <InstanceRow
                          key={view.instance.id}
                          view={view}
                          snapshot={snapshot}
                          now={now}
                          {...(onRecordPayment
                            ? { onRecordPayment: () => onRecordPayment(h.bill, view.instance) }
                            : {})}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function InstanceRow({
  view,
  snapshot,
  now,
  onRecordPayment,
}: {
  view: BillInstanceView
  snapshot: MoneySnapshot
  now: number
  onRecordPayment?: () => void
}) {
  const { instance, payments, paid, outstanding, status, overdue } = view
  return (
    <li className="m-instance" data-testid={`instance-${instance.id}`}>
      <div className="m-instance-head">
        <span className="m-instance-period">{instance.periodLabel}</span>
        <StatusBadge status={status} overdue={overdue} />
      </div>
      <p className="m-instance-line">
        Due {formatDay(instance.dueDay)}
        <span className={overdue ? 'm-due-late' : 'm-due'}> · {describeDue(instance.dueDay, now)}</span>
      </p>
      <dl className="m-facts">
        <div>
          <dt>Expected</dt>
          <dd><Money value={instance.amountDue} /></dd>
        </div>
        <div>
          <dt>Paid</dt>
          <dd><Money value={paid} /></dd>
        </div>
        <div>
          <dt>Outstanding</dt>
          <dd>
            <Money
              value={outstanding}
              tone={outstanding > 0 ? (overdue ? 'warn' : 'neutral') : 'ok'}
            />
          </dd>
        </div>
      </dl>

      {payments.length === 0 ? (
        <p className="m-empty-note">Nothing paid yet.</p>
      ) : (
        <ul className="m-payments" aria-label={`Payments for ${instance.periodLabel}`}>
          {payments.map(p => (
            <li key={p.id}>
              <Money value={p.amount} /> · {formatInstant(p.paidAt)} · {nameOf(snapshot, p.paidByMemberId)}
              {' · '}
              {p.method}
              {snapshot.pendingIds.has(p.id) && <span className="m-tag"> not yet synced</span>}
            </li>
          ))}
        </ul>
      )}

      {onRecordPayment && (
        <button type="button" className="btn m-instance-action" onClick={onRecordPayment}>
          Record a payment for {instance.periodLabel}
        </button>
      )}
    </li>
  )
}
